import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, Legend
} from 'recharts';
import moment from 'moment';
import '../farmDashboard.css';
import '../farmDashboardV2.css';
import { stagger, onEnter, onLeave } from '../lib/stagger';
import Sensor3DView from './Sensor3DView';
import CalibrationPage from './CalibrationPage';
import { UpcomingDeliveryCard, subscriptionUrgency } from '../components/SubscriptionDeliveryCard';
import { OrderDeliveryCard, orderUrgency } from '../components/OrderDeliveryCard';
import CommanderStatusPanel from '../components/CommanderStatusPanel';
import BriefingCard from '../components/BriefingCard';
import { PageHeader } from '../lib/ui';

// ---------------- Constants ----------------
const API_BASE = 'https://teardrophydro.co.uk/garageFarm';

const FLOOD_CFG = {
  lights: { startHour: 0, endHour: 16 },
  flooding: {
    slotMinutes: 30,
    offsetMinutes: 5,   // server fires 5 min into each slot (:05 and :35)
    layerBySlot: [5, 4, 3, 2, 1, null, null],
    durationsMs: { 1: 60000, 2: 40000, 3: 70000, 4: 80000, 5: 100000, 6: 60000, 7: 60000 },
  },
};

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});
axios.defaults.headers.common['Content-Type'] = 'application/json';

const RANGES = {
  pH: [5.5, 6.8],
  EC: [1.5, 2.0],
};

const VIEW_TABS = [
  { key: 'irrigation', label: 'Irrigation & Dosing' },
  { key: 'charts', label: 'Sensor Charts' },
  { key: '3d', label: '3D Room View' },
  { key: 'calibration', label: 'Calibration' },
];

const RANGE_OPTIONS = [
  { limit: 2400, label: 'Last Day' },
  { limit: 16800, label: 'Last Week' },
  { limit: 33600, label: 'Last 2 Weeks' },
];

// ---------------- Math & Helper Functions (Pure) ----------------

const pad2 = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const fmtTime = (ts) => moment(ts).format('HH:mm');

// Helper to format numbers to exactly 2 decimal places (or '-'/null)
const fmt2DP = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v.toFixed(2) : '-';

function downsample(data, targetCount = 1000) {
  if (!data || data.length <= targetCount) return data;
  const step = Math.ceil(data.length / targetCount);
  return data.filter((_, i) => i % step === 0);
}

// A CO2 reading of 0 is a sensor dropout, not a real value
const dropZeroCO2 = (pt) => (pt.CO2 === 0 ? { ...pt, CO2: null } : pt);

const rollingMedian = (data, key, w = 9) => {
  const half = Math.floor(w / 2);
  return data.map((pt, i) => {
    const start = Math.max(0, i - half);
    const end = Math.min(data.length, i + half + 1);
    const win = [];
    for (let k = start; k < end; k++) {
      const v = data[k][key];
      if (typeof v === 'number' && !Number.isNaN(v)) win.push(v);
    }
    if (!win.length) return { ...pt, [`${key}_med`]: null };
    win.sort((a, b) => a - b);
    const mid = Math.floor(win.length / 2);
    const med = win.length % 2 ? win[mid] : (win[mid - 1] + win[mid]) / 2;
    return { ...pt, [`${key}_med`]: med };
  });
};

const hampel = (data, keyMed, w = 9, k = 3) => {
  const half = Math.floor(w / 2);
  return data.map((pt, i, arr) => {
    const val = pt[keyMed];
    if (typeof val !== 'number') return { ...pt, [`${keyMed}_hf`]: null };
    const start = Math.max(0, i - half);
    const end = Math.min(arr.length, i + half + 1);
    const win = [];
    for (let j = start; j < end; j++) {
      const v = arr[j][keyMed];
      if (typeof v === 'number') win.push(v);
    }
    if (!win.length) return { ...pt, [`${keyMed}_hf`]: null };

    win.sort((a, b) => a - b);
    const midIdx = Math.floor(win.length / 2);
    const med = win.length % 2 ? win[midIdx] : (win[midIdx - 1] + win[midIdx]) / 2;

    const absDevs = win.map(v => Math.abs(v - med)).sort((a, b) => a - b);
    const mad = absDevs.length % 2 ? absDevs[midIdx] : (absDevs[midIdx - 1] + absDevs[midIdx]) / 2;

    const limit = k * 1.4826 * mad;
    const isOutlier = limit > 0 && Math.abs(val - med) > limit;
    return { ...pt, [`${keyMed}_hf`]: isOutlier ? med : val };
  });
};

const ema = (data, key, alpha = 0.2) => {
  let last = null;
  return data.map(pt => {
    const v = pt[key];
    if (typeof v !== 'number' || Number.isNaN(v)) return { ...pt, [`${key}_ema`]: last };
    last = (last == null) ? v : (alpha * v + (1 - alpha) * last);
    return { ...pt, [`${key}_ema`]: last };
  });
};

function computeFloodScheduleForDate(cfg, date = new Date()) {
  const { startHour, endHour } = cfg.lights;
  const { slotMinutes, offsetMinutes = 0, layerBySlot, durationsMs } = cfg.flooding;
  const dayStart = new Date(date); dayStart.setHours(startHour, 0, 0, 0);
  const dayEnd   = new Date(date); dayEnd.setHours(endHour,   0, 0, 0);
  const totalMin = (endHour - startHour) * 60;
  const totalSlots = Math.floor(totalMin / slotMinutes);
  const rows = [];
  for (let i = 0; i < totalSlots; i++) {
    // Shift each fire time by offsetMinutes so display matches server behaviour (:05, :35, …)
    const fireTime = new Date(dayStart.getTime() + i * slotMinutes * 60_000 + offsetMinutes * 60_000);
    if (fireTime >= dayEnd) break;
    const patternIdx = i % layerBySlot.length;
    const layer = layerBySlot[patternIdx];
    if (layer === null) continue;
    rows.push({
      slot: i + 1,
      start: fireTime,
      layer,
      durationMs: durationsMs[String(layer)] ?? 50_000,
    });
  }
  return rows;
}

function getPrevNextFlood(cfg, now = new Date()) {
  const todayRows = computeFloodScheduleForDate(cfg, now);
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const tRows = computeFloodScheduleForDate(cfg, tomorrow);

  let next = todayRows.find(r => r.start > now);
  if (!next && tRows.length) next = tRows[0];

  const inMinutes = next ? Math.max(0, Math.round((next.start - now) / 60_000)) : null;
  let etaStr = null;
  if (inMinutes !== null) {
    const h = Math.floor(inMinutes / 60);
    const m = inMinutes % 60;
    etaStr = inMinutes < 60 ? `${inMinutes} min` : (m === 0 ? `${h} hr` : `${h} hr ${m} min`);
  }
  return { next, inMinutes, etaStr };
}

// --- Sub-Components ---

const CustomTooltip = React.memo(({ active, payload, label }) => {
  if (!(active && payload && payload.length)) return null;
  return (
    <div className="fd2-tooltip">
      <p className="fd2-tooltip-label">{`Time: ${fmtTime(label)}`}</p>
      {payload.filter(e => e.value != null).map((e, i) => (
        <p key={i} className="fd2-tooltip-item" style={{ color: e.color || e.stroke }}>
          {`${e.name}: ${typeof e.value === 'number' ? (e.value >= 50 ? e.value.toFixed(0) : e.value.toFixed(2)) : e.value}`}
        </p>
      ))}
    </div>
  );
});

// One "at a glance" tile - EC / pH / Air / Water all render through this.
function StatTile({ icon, label, tone = 'neutral', i, children }) {
  return (
    <li className={`fd2-tile fd2-tile--${tone}`} style={stagger(i)}>
      <div className="fd2-tile-icon" aria-hidden="true">{icon}</div>
      <div className="fd2-tile-body">
        <div className="fd2-tile-label">{label}</div>
        {children}
      </div>
    </li>
  );
}

// Next-flood tile - lives in the snapshot strip alongside the sensor readings
// so the whole "what's happening right now" picture is in one place.
const FloodTile = ({ i }) => {
  const [state, setState] = useState(() => getPrevNextFlood(FLOOD_CFG));
  useEffect(() => {
    const t = setInterval(() => setState(getPrevNextFlood(FLOOD_CFG)), 15_000);
    return () => clearInterval(t);
  }, []);
  const { next, etaStr } = state;
  const slotMs = FLOOD_CFG.flooding.slotMinutes * 60_000;
  const pct = next ? Math.max(0, Math.min(100, 100 - ((next.start - Date.now()) / slotMs) * 100)) : 0;

  return (
    <StatTile icon="⏱️" label="Next Flood" i={i}>
      {next ? (
        <>
          <div className="fd2-value">
            <span className="fd2-reading">L{next.layer}<span className="fd2-unit">{hhmm(next.start)}</span></span>
            <span className="fd2-sep">•</span><span className="fd2-reading fd2-unit">in {etaStr}</span>
          </div>
          <div className="fd2-bar"><i style={{ width: `${pct}%` }} /></div>
        </>
      ) : <div className="fd2-value">-</div>}
    </StatTile>
  );
};

// Sensor readings + flood countdown, shown at the top of the Sensor Charts
// tab - this is the "glanceable" heart of the dashboard.
const SnapshotStrip = React.memo(({ latest }) => {
  const band = (val, [lo, hi]) => {
    if (typeof val !== 'number' || !Number.isFinite(val)) return 'unknown';
    if (val < lo || val > hi) return 'bad';
    const tol = (hi - lo) * 0.07;
    if (val - lo < tol || hi - val < tol) return 'warn';
    return 'ok';
  };
  const fmt = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '-');
  const ecState = latest ? band(latest.EC, RANGES.EC) : 'unknown';
  const phState = latest ? band(latest.pH, RANGES.pH) : 'unknown';

  return (
    <ul className="fd2-snapshot">
      {latest && (
        <>
          <StatTile icon="🧂" label="EC" tone={ecState} i={0}>
            <div className="fd2-value">{fmt(latest.EC)} <span className="fd2-unit">mS/cm</span></div>
            {ecState === 'bad' && <span className="fd2-alert">out of band</span>}
          </StatTile>
          <StatTile icon="🧪" label="pH" tone={phState} i={1}>
            <div className="fd2-value">{fmt(latest.pH)}</div>
            {phState === 'bad' && <span className="fd2-alert">out of band</span>}
          </StatTile>
          <StatTile icon="💨" label="Air" i={2}>
            <div className="fd2-value">
              <span className="fd2-reading">{fmt(latest.avgTemp)}<span className="fd2-unit">°C</span></span>
              <span className="fd2-sep">•</span>
              <span className="fd2-reading">{fmt(latest.avgHumidity)}<span className="fd2-unit">%</span></span>
              <span className="fd2-sep">•</span>
              <span className="fd2-reading">{latest.CO2?.toFixed(0) || '-'}<span className="fd2-unit">ppm</span></span>
            </div>
          </StatTile>
          <StatTile icon="💧" label="Water" i={3}>
            <div className="fd2-value">
              <span className="fd2-reading">{fmt(latest.waterTempC)}<span className="fd2-unit">°C</span></span>
              <span className="fd2-sep">•</span>
              <span className="fd2-reading">{typeof latest.waterLevel === 'number' ? latest.waterLevel.toFixed(1) : '-'}<span className="fd2-unit">cm</span></span>
            </div>
          </StatTile>
        </>
      )}
      <FloodTile i={4} />
    </ul>
  );
});

// One column of compact delivery cards - Today and Tomorrow render one of
// these each, stacked in the sidebar.
const SubscriptionColumn = ({ title, deliveries, onRefresh }) => {
  if (!deliveries.length) return null;
  const overdueCount = deliveries.filter(d => subscriptionUrgency(d).isOverdue).length;
  return (
    <div className="fd2-subcol" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="fd2-subcol-head">
        <h3 className="fd2-subcol-title">{title}</h3>
        <span className="fd2-subcol-count">
          {deliveries.length} due{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
        </span>
      </div>
      <div className="fd2-subcol-list">
        {deliveries.map(sub => (
          <UpcomingDeliveryCard key={sub._id} sub={sub} onDone={onRefresh} compact />
        ))}
      </div>
    </div>
  );
};

// One-off (non-subscription) orders that have come in but not yet gone out.
const OrderColumn = ({ title, orders, onRefresh }) => {
  if (!orders.length) return null;
  const overdueCount = orders.filter(o => orderUrgency(o).isOverdue).length;
  return (
    <div className="fd2-subcol" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="fd2-subcol-head">
        <h3 className="fd2-subcol-title">{title}</h3>
        <span className="fd2-subcol-count">
          {orders.length} due{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
        </span>
      </div>
      <div className="fd2-subcol-list">
        {orders.map(order => (
          <OrderDeliveryCard key={order._id} order={order} onDone={onRefresh} compact />
        ))}
      </div>
    </div>
  );
};

// ---------------- Main Dashboard ----------------
const FarmDashboard = () => {
  const [rawSensorData, setRawSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [limit, setLimit] = useState(2400);
  const [view, setView] = useState('charts'); // 'irrigation' | 'charts' | '3d' | 'calibration'
  const [subToday, setSubToday] = useState([]);
  const [subTomorrow, setSubTomorrow] = useState([]);
  const [unfulfilledOrders, setUnfulfilledOrders] = useState([]);

  const [floodMs, setFloodMs] = useState(60000);
  const [ecMs, setEcMs] = useState(30000);
  const [acidMs, setAcidMs] = useState(15000);

  /* UI States for inline alerts & custom entry */
  const [customMsInput, setCustomMsInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', isError: false });

  // Clear notification messages automatically after 4 seconds
  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => setFeedback({ message: '', isError: false }), 4000);
      return () => clearTimeout(timer);
    }
  }, [feedback.message]);

  const normalizeCommand = (uiType) => {
    if (uiType.startsWith('FLOOD_')) {
      const layer = Number(uiType.split('_')[1]) || 1;
      return { type: 'FLOOD', layer };
    }
    if (uiType === 'INCREASE_EC') return { type: 'DOSE_EC' };
    if (uiType === 'LOWER_PH')   return { type: 'DOSE_PH_DOWN' };
    return { type: uiType };
  };

  const handleRunCommand = async (uiType, override = {}) => {
    try {
      const base = normalizeCommand(uiType);
      let payload = { ...base, ...override };

      // Set defaults if no override was manually passed down
      if (override.durationMs == null) {
        if (base.type === 'FLOOD') {
          const defaults = { 1: 40000, 2: 70000, 3: 50000, 4: 50000 };
          payload.durationMs = defaults[base.layer] || floodMs;
        } else if (base.type === 'DOSE_EC') {
          payload.durationMs = ecMs;
        } else if (base.type === 'DOSE_PH_DOWN') {
          payload.durationMs = acidMs;
        }
      }

      await axios.post(`${API_BASE}/commands`, payload);
      setFeedback({
        message: `✅ ${uiType} queued for ${Math.round(payload.durationMs / 1000)}s!`,
        isError: false
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message;
      setFeedback({ message: `❌ Failed: ${msg}`, isError: true });
    }
  };

  const applyCustomDuration = (e) => {
    e.preventDefault();
    const parsed = parseInt(customMsInput, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      setFloodMs(parsed * 1000);
      setShowCustomInput(false);
      setCustomMsInput('');
    } else {
      setFeedback({ message: 'Please enter a duration in seconds (minimum 1s)', isError: true });
    }
  };

  const fetchData = useCallback(async () => {
    const controller = new AbortController();
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/sensor-data/verbose?limit=${limit}`, {
        signal: controller.signal
      });

      const sorted = (res.data || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      const cleaned = sorted.map((entry) => {
        let sumT = 0, cntT = 0, sumH = 0, cntH = 0;
        const sObj = {};
        for (let i = 0; i < 16; i++) {
          const s = entry[`sensor${i}`];
          if (s) {
            const validT = (typeof s.temp === 'number' && s.temp > 10 && s.temp < 40);
            const validH = (typeof s.humidity === 'number' && s.humidity > 10 && s.humidity < 100);
            if (validT) { sumT += s.temp; cntT++; }
            if (validH) { sumH += s.humidity; cntH++; }
            sObj[`sensor${i}.temp`] = validT ? s.temp : null;
            sObj[`sensor${i}.humidity`] = validH ? s.humidity : null;
          }
        }
        const rawSonar = entry.sonarCM;
        let wl = 50;
        if (typeof rawSonar === 'number' && rawSonar > 0 && rawSonar < 90) wl = Math.max(0, 90 - rawSonar);

        const estPH = entry.pH?.est;
        const estEC = entry.EC?.est;

        return {
          ...entry,
          ...sObj,
          timestamp: new Date(entry.timestamp).getTime(),
          pH: (typeof estPH === 'number' && estPH >= 2 && estPH <= 14) ? estPH : null,
          EC: (typeof estEC === 'number' && estEC >= 0 && estEC < 10) ? estEC : null,
          waterTempC: (typeof entry.waterTempC === 'number' && entry.waterTempC > 0 && entry.waterTempC < 50) ? entry.waterTempC : null,
          waterLevel: wl,
          avgTemp: cntT ? sumT / cntT : null,
          avgHumidity: cntH ? sumH / cntH : null,
        };
      });

      setRawSensorData(cleaned);
      if (cleaned.length) setLastUpdated(moment(cleaned[cleaned.length - 1].timestamp).format('YYYY-MM-DD HH:mm:ss'));
      setError('');
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
        setError('Failed to fetch data.');
      }
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, [limit]);

  useEffect(() => {
    const cancel = fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => { clearInterval(interval); if (typeof cancel === 'function') cancel(); };
  }, [fetchData]);

  const fetchSubDeliveries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/subscriptions/analytics`);
      const upcoming = res.data?.upcomingDeliveries || [];
      setSubToday(upcoming.filter(sub => {
        const { isToday, isOverdue } = subscriptionUrgency(sub);
        return isToday || isOverdue;
      }));
      setSubTomorrow(upcoming.filter(sub => subscriptionUrgency(sub).isTomorrow));
    } catch (err) { console.error('Subscription deliveries fetch failed', err); }
  }, []);

  useEffect(() => {
    fetchSubDeliveries();
    const interval = setInterval(fetchSubDeliveries, 5 * 60_000);
    return () => clearInterval(interval);
  }, [fetchSubDeliveries]);

  const fetchUnfulfilledOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/orders/unfulfilled`);
      setUnfulfilledOrders(res.data || []);
    } catch (err) { console.error('Unfulfilled orders fetch failed', err); }
  }, []);

  useEffect(() => {
    fetchUnfulfilledOrders();
    const interval = setInterval(fetchUnfulfilledOrders, 5 * 60_000);
    return () => clearInterval(interval);
  }, [fetchUnfulfilledOrders]);

  // --- Processing Pipeline (Memoized) ---
  const { chartData, latest } = useMemo(() => {
    if (!rawSensorData.length) return { chartData: [], latest: null };

    const s1 = rollingMedian(rawSensorData, 'pH', 11);
    const s2 = hampel(s1, 'pH_med', 11, 3);
    const smoothed = ema(s2, 'pH_med_hf', 0.25);
    const finalData = downsample(smoothed, 1200).map(dropZeroCO2);

    return {
      chartData: finalData,
      latest: dropZeroCO2(rawSensorData[rawSensorData.length - 1])
    };
  }, [rawSensorData]);

  // --- Helpers for Charts ---
  const getDomain = (key) => {
    const vals = chartData.map(d => d[key]).filter(v => typeof v === 'number');
    if (!vals.length) return ['auto', 'auto'];
    const min = Math.min(...vals), max = Math.max(...vals);
    const pad = Math.max(0.1, (max - min) * 0.1);
    return [min - pad, max + pad];
  };

  const cardStyle = (i) => ({ ...stagger(i), transition: 'transform 200ms ease, box-shadow 200ms ease' });

  const floodWindows = computeFloodScheduleForDate(FLOOD_CFG).map(r => ({
    x1: r.start.getTime(), x2: r.start.getTime() + r.durationMs
  }));

  if (loading && !chartData.length) return <div className="fd2-loading"><div className="fd2-spinner"></div></div>;
  if (error) return <div className="fd2-error">{error}</div>;

  const hasSidebar = subToday.length > 0 || subTomorrow.length > 0 || unfulfilledOrders.length > 0;

  return (
    <div className="fd2-page">
      <PageHeader
        title="Farm"
        meta={(
          <div className="fd2-live">
            <span className="fd2-pulse" />
            <span>{lastUpdated ? `Live · ${lastUpdated.slice(11, 16)}` : 'Connecting…'}</span>
          </div>
        )}
        tabs={VIEW_TABS}
        activeTab={view}
        onTabChange={setView}
      />

      <div className={`fd2-body ${hasSidebar ? '' : 'fd2-body--no-sidebar'}`}>
        <div className="fd2-main">

          {view === 'irrigation' && (
            <div className="fd2-panel">
              <div className="fd2-ctrl">

                {/* STATUS NOTIFICATION ZONE (Only displays when active) */}
                {feedback.message && (
                  <div className={`fd2-banner ${feedback.isError ? 'is-error' : 'is-success'}`}>
                    {feedback.message}
                  </div>
                )}

                {/* DURATION PRESETS */}
                <div className="fd2-ctrl-row">
                  <span className="fd2-eyebrow">Flood Duration</span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="fd2-seg">
                      {([30000, 60000, 90000]).map(ms => (
                        <button
                          key={ms}
                          className={ms === floodMs ? 'is-active' : ''}
                          onClick={() => setFloodMs(ms)}
                          type="button"
                        >
                          {Math.round(ms / 1000)}s
                        </button>
                      ))}
                      <button
                        className={showCustomInput ? 'is-active' : ''}
                        onClick={() => setShowCustomInput(!showCustomInput)}
                        type="button"
                      >
                        Custom ⋯
                      </button>
                    </div>

                    {showCustomInput && (
                      <form onSubmit={applyCustomDuration} className="fd2-custom-form">
                        <input
                          type="number"
                          placeholder="Duration (s)"
                          value={customMsInput}
                          onChange={(e) => setCustomMsInput(e.target.value)}
                        />
                        <button type="submit">Apply</button>
                      </form>
                    )}
                  </div>
                </div>

                {/* FLOOD VALVES */}
                <div className="fd2-ctrl-grid">
                  {[1, 2, 3, 4, 5].map(layer => (
                    <button
                      key={layer}
                      className="fd2-ctrl-btn fd2-ctrl-btn--flood"
                      onClick={() => handleRunCommand(`FLOOD_${layer}`, { durationMs: floodMs })}
                      type="button"
                    >
                      <span className="fd2-ctrl-emoji">💧</span>
                      <span className="fd2-ctrl-label">Flood L{layer}</span>
                      <span className="fd2-ctrl-meta">{Math.round(floodMs / 1000)}s</span>
                    </button>
                  ))}
                </div>

                {/* DOSING RIG */}
                <div className="fd2-ctrl-row">
                  <span className="fd2-eyebrow">Chemical Dosing</span>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 160 }}>
                      <span className="fd2-eyebrow">EC Duration</span>
                      <div className="fd2-seg">
                        {[30000, 60000, 120000].map(ms => (
                          <button key={ms} type="button"
                            className={ms === ecMs ? 'is-active' : ''}
                            onClick={() => setEcMs(ms)}>
                            {ms / 1000}s
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 160 }}>
                      <span className="fd2-eyebrow">pH Duration</span>
                      <div className="fd2-seg">
                        {[5000, 15000, 30000].map(ms => (
                          <button key={ms} type="button"
                            className={ms === acidMs ? 'is-active' : ''}
                            onClick={() => setAcidMs(ms)}>
                            {ms / 1000}s
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="fd2-ctrl-grid">
                    <button
                      className="fd2-ctrl-btn fd2-ctrl-btn--ec"
                      onClick={() => handleRunCommand('INCREASE_EC', { durationMs: ecMs })}
                      type="button"
                    >
                      <span className="fd2-ctrl-emoji">⚗️</span>
                      <span className="fd2-ctrl-label">Increase EC</span>
                      <span className="fd2-ctrl-meta">{Math.round(ecMs / 1000)}s</span>
                    </button>

                    <button
                      className="fd2-ctrl-btn fd2-ctrl-btn--ph"
                      onClick={() => handleRunCommand('LOWER_PH', { durationMs: acidMs })}
                      type="button"
                    >
                      <span className="fd2-ctrl-emoji">🧪</span>
                      <span className="fd2-ctrl-label">Lower pH</span>
                      <span className="fd2-ctrl-meta">{Math.round(acidMs / 1000)}s</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {view === '3d' && <div className="fd2-panel"><Sensor3DView /></div>}
          {view === 'calibration' && <div className="fd2-panel"><CalibrationPage /></div>}

          {view === 'charts' && (
            <>
              <BriefingCard />
              <SnapshotStrip latest={latest} />
              <CommanderStatusPanel />

              {/* <div className="fd2-section-head">
                <h2 className="fd2-section-title">Sensor history</h2>
                <div className="fd2-seg">
                  {RANGE_OPTIONS.map(({ limit: v, label }) => (
                    <button key={v} onClick={() => setLimit(v)} className={limit === v ? 'is-active' : ''} type="button">
                      {label}
                    </button>
                  ))}
                </div>
              </div> */}

              <div className="fd2-chart-grid">

                {/* 1. EC & Water Level */}
                <div className="fd2-chart-card" style={cardStyle(0)} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  <h3 className="fd2-chart-title" style={stagger(0, 'td-fade-down')}>EC & Water Level</h3>
                  <ResponsiveContainer height={240} width="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" tickFormatter={fmtTime} type="number" scale="time" domain={['auto', 'auto']} />
                      {/* EC YAxis: 2dp */}
                      <YAxis yAxisId="l" domain={[1.6, 2.2]} tickFormatter={(v) => v.toFixed(2)} />
                      {/* Water Level YAxis: 2dp (though data is usually 1dp, this ensures consistency) */}
                      <YAxis yAxisId="r" orientation="right" domain={[40, 70]} tickFormatter={fmt2DP} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {floodWindows.map((w, i) => <ReferenceArea key={i} yAxisId="l" x1={w.x1} x2={w.x2} fill="rgba(24,150,247,.06)" />)}
                      <Line yAxisId="r" type="monotone" dataKey="waterLevel" name="Level" stroke="#0ea5e9" dot={false} strokeWidth={2.2} />
                      <Line yAxisId="l" type="monotone" dataKey="EC" name="EC" stroke="#6d67e4" dot={false} strokeWidth={1.8} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* 2. CO2 */}
                <div className="fd2-chart-card" style={cardStyle(1)} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  <h3 className="fd2-chart-title" style={stagger(0, 'td-fade-down')}>Carbon Dioxide levels</h3>
                  <ResponsiveContainer height={240} width="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" tickFormatter={fmtTime} type="number" scale="time" domain={['auto', 'auto']} />
                      <YAxis domain={getDomain('CO2')} tickFormatter={v => v.toFixed(0)} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="CO2" stroke="#e36300" dot={false} strokeWidth={2.2} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* 3. pH & Water Temp */}
                <div className="fd2-chart-card" style={cardStyle(2)} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  <h3 className="fd2-chart-title" style={stagger(0, 'td-fade-down')}>pH Level & Water Temp</h3>
                  <ResponsiveContainer height={240} width="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" tickFormatter={fmtTime} type="number" scale="time" domain={['auto', 'auto']} />
                      {/* pH YAxis: 2dp */}
                      <YAxis yAxisId="l" domain={[5.5, 6.5]} tickFormatter={(v) => v.toFixed(2)} />
                      {/* Water Temp YAxis: 2dp */}
                      <YAxis yAxisId="r" orientation="right" domain={getDomain('waterTempC')} tickFormatter={fmt2DP} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceArea yAxisId='l' y1={5.8} y2={6.2} fill="rgba(0, 255, 0, 0.1)" />
                      <Line yAxisId="r" type="monotone" dataKey="waterTempC" name="Water Temp" stroke="#0077ff" strokeWidth={3.5} dot={false} />
                      <Line yAxisId="l" type="monotone" dataKey="pH" name="pH (raw)" stroke="#9adbb0" strokeWidth={1} dot={false} opacity={0.35} />
                      <Line yAxisId="l" type="monotone" dataKey="pH_med_hf_ema" name="pH (smooth)" stroke="#2fbf71" strokeWidth={2.4} dot={false} />
                      <Legend />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* 4. Humidity & Air Temp */}
                <div className="fd2-chart-card" style={cardStyle(3)} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  <h3 className="fd2-chart-title" style={stagger(0, 'td-fade-down')}>Humidity & Air Temp</h3>
                  <ResponsiveContainer height={240} width="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" tickFormatter={fmtTime} type="number" scale="time" domain={['auto', 'auto']} />
                      {/* Humidity YAxis: 2dp */}
                      <YAxis yAxisId="l" domain={[40, 80]} tickFormatter={fmt2DP} />
                      {/* Air Temp YAxis: 2dp */}
                      <YAxis yAxisId="r" orientation="right" domain={getDomain('avgTemp')} tickFormatter={fmt2DP} />
                      <Tooltip content={<CustomTooltip />} />
                      {[...Array(15)].map((_, i) => (
                        <Line key={i} yAxisId="l" type="monotone" dataKey={`sensor${i}.humidity`} stroke={`hsl(${(i * 45 + 180) % 360}, 70%, 60%)`} dot={false} strokeWidth={0.7} opacity={0.5} name={`S${i}`} />
                      ))}
                      <Line yAxisId="l" type="monotone" dataKey="avgHumidity" name="Avg Hum" stroke="#113449" strokeWidth={3.5} dot={false} />
                      <Line yAxisId="r" type="monotone" dataKey="avgTemp" name="Avg Temp" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

              </div>
            </>
          )}
        </div>

        {hasSidebar && (
          <div className="fd2-sidebar">
            <div className="fd2-sidebar-head">
              <h2 className="fd2-sidebar-title">Deliveries</h2>
            </div>
            <OrderColumn title="Orders Awaiting Delivery" orders={unfulfilledOrders} onRefresh={fetchUnfulfilledOrders} />
            <SubscriptionColumn title="Today's Subscriptions" deliveries={subToday} onRefresh={fetchSubDeliveries} />
            <SubscriptionColumn title="Tomorrow's Subscriptions" deliveries={subTomorrow} onRefresh={fetchSubDeliveries} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmDashboard;
